import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { resetConfiguratorV2Step, updateConfiguratorV2 } from "../data/configuratorV2Api";
import type { ConfiguratorV2StepResetRequest, ConfiguratorV2UpdateRequest } from "../data/configuratorV2Api.types";
import {
  configuratorConfigurationKey,
  configuratorMutationKey,
  type ConfiguratorRuntimeQueryData,
} from "../data/configuratorConfigurationQuery";
import { adaptConfiguratorConfiguration } from "../data/adaptConfiguratorConfiguration";
import { deleteConfiguratorAttachment } from "../data/configuratorAttachmentApi";
import type { ConfiguratorConfiguration } from "../contract/configuration";
import type { ConfiguratorDefinition } from "../contract/definition";
import type { ResolvedControlModel } from "../engine/deriveConfiguratorModel";
import { buildStepResetRequest } from "../engine/reset/buildStepResetRequest";
import { withConfiguratorMutationLock } from "./configuratorMutationLock";

const includesAttachment = (definition: ConfiguratorDefinition, stepIndex: number) => {
  const attachmentStepIndex = definition.steps.findIndex((step) =>
    step.substeps.some((substep) => substep.renderer === "configurationAttachment"),
  );
  if (attachmentStepIndex < 0) {
    return false;
  }

  return definition.stepResetScope === "stepOnly"
    ? stepIndex === attachmentStepIndex
    : stepIndex <= attachmentStepIndex;
};

const deleteAttachmentIfIncluded = async (productCode: string, configId: string, included: boolean) => {
  if (!included) {
    return;
  }

  try {
    await deleteConfiguratorAttachment(productCode, configId);
  } catch (error) {
    if (typeof error !== "object" || error === null || !("status" in error) || error.status !== 404) {
      throw error;
    }
  }
};

export const useConfiguratorUpdate = (definition: ConfiguratorDefinition, configuration: ConfiguratorConfiguration) => {
  const queryClient = useQueryClient();
  const assertConfigurationIdentity = useCallback(() => {
    const current = queryClient.getQueryData<ConfiguratorRuntimeQueryData>(
      configuratorConfigurationKey(configuration.productCode),
    );
    if (current && current.configuration.configId !== configuration.configId) {
      throw new Error("Configurator mutation belongs to a superseded configuration.");
    }
  }, [configuration.configId, configuration.productCode, queryClient]);
  const replaceConfiguration = useCallback(
    ({ data }: Awaited<ReturnType<typeof updateConfiguratorV2>>) => {
      queryClient.setQueryData<ConfiguratorRuntimeQueryData>(
        configuratorConfigurationKey(configuration.productCode),
        (current) => {
          if (!current || current.configuration.configId !== configuration.configId) {
            return current;
          }
          return {
            ...current,
            configuration: {
              ...adaptConfiguratorConfiguration(data),
              helpUrl: current.context.helpUrl,
            },
          };
        },
      );
    },
    [configuration.configId, configuration.productCode, queryClient],
  );
  const mutateConfiguration = useCallback(
    (request: ConfiguratorV2UpdateRequest) =>
      withConfiguratorMutationLock(configuration.configId, async () => {
        assertConfigurationIdentity();
        return updateConfiguratorV2(request);
      }),
    [assertConfigurationIdentity, configuration.configId],
  );
  const {
    error: updateError,
    isPending: updatePending,
    mutate: update,
  } = useMutation({
    mutationKey: configuratorMutationKey(configuration.productCode),
    mutationFn: mutateConfiguration,
    onSuccess: replaceConfiguration,
  });
  const {
    error: initializationError,
    isPending: initializationPending,
    mutate: initialize,
    variables: initializationVariables,
  } = useMutation({
    mutationKey: configuratorMutationKey(configuration.productCode),
    mutationFn: mutateConfiguration,
    onSuccess: replaceConfiguration,
  });
  const {
    error: resetError,
    isPending: resetPending,
    mutate: reset,
  } = useMutation({
    mutationKey: configuratorMutationKey(configuration.productCode),
    mutationFn: async (request: ConfiguratorV2StepResetRequest) =>
      withConfiguratorMutationLock(configuration.configId, async () => {
        assertConfigurationIdentity();
        await deleteAttachmentIfIncluded(
          configuration.productCode,
          configuration.configId,
          includesAttachment(definition, request.stepNumber - 1),
        );
        return resetConfiguratorV2Step(request);
      }),
    onSuccess: replaceConfiguration,
  });

  const updateControl = useCallback(
    (stepIndex: number, control: ResolvedControlModel, value: string | null) =>
      update({
        configId: configuration.configId,
        currentStepNumber: stepIndex + 1,
        navigation: false,
        changes: [
          {
            instanceId: control.context.instance.id,
            csticName: control.definition.cstic,
            value,
          },
        ],
      }),
    [configuration.configId, update],
  );

  const initializeStep = useCallback(
    (stepIndex: number) =>
      initialize({
        configId: configuration.configId,
        currentStepNumber: stepIndex + 1,
        navigation: true,
        changes: [],
      }),
    [configuration.configId, initialize],
  );

  const retryInitialization = useCallback(() => {
    if (initializationVariables) {
      initialize(initializationVariables);
    }
  }, [initializationVariables, initialize]);

  const resetStep = useCallback(
    (stepIndex: number) => reset(buildStepResetRequest(definition, configuration, stepIndex)),
    [configuration, definition, reset],
  );

  return {
    error: updateError ?? initializationError ?? resetError,
    initializationError,
    isUpdating: updatePending || initializationPending || resetPending,
    initializeStep,
    resetStep,
    retryInitialization,
    updateControl,
  };
};
